import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Switch,
  FormControlLabel,
  IconButton,
  Tabs,
  Tab,
  Paper,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Slide,
  Alert,
  Fade,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Verified as VerifiedIcon,
  Star as StarIcon,
  Diamond as DiamondIcon,
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Autorenew as AutorenewIcon,
  ErrorOutline as ErrorIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Compare as CompareIcon,
  Block as BlockIcon,
  Wallet as WalletIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import useApp from "../../store/contexts/AppContext";
import { SNACKBAR_VARIANT } from "../../config";
import { USER_ACCOUNT_TYPES, TIER_COLORS, TIER_ICONS, DEFAULT_TIER_BENEFITS } from "../../constants/types";
import axios from "axios";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Confirmation dialog component for actions
const ConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  loading,
  title,
  message,
  confirmText,
  cancelText,
  confirmColor,
}) => {
  return (
    <Dialog open={open} onClose={!loading ? onClose : undefined} maxWidth="xs">
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <ErrorIcon sx={{ color: `${confirmColor}.main`, mr: 1 }} />
          {title}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography>{message}</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={loading}>
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          color={confirmColor}
          variant="contained"
          disabled={loading}
          startIcon={
            loading ? <CircularProgress size={20} color="inherit" /> : null
          }
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Calculate time remaining in a friendly format
const calculateTimeRemaining = (endDate) => {
  if (!endDate) return null;
  const now = new Date();
  const end = new Date(endDate);
  const diffTime = end - now;
  if (diffTime <= 0) return "Expired";

  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(
    (diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );

  if (diffDays > 0) {
    return `${diffDays} day${diffDays !== 1 ? "s" : ""} ${
      diffHours > 0 ? `${diffHours} hr${diffHours !== 1 ? "s" : ""}` : ""
    }`;
  } else if (diffHours > 0) {
    return `${diffHours} hour${diffHours !== 1 ? "s" : ""}`;
  } else {
    const diffMinutes = Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60));
    return `${diffMinutes} minute${diffMinutes !== 1 ? "s" : ""}`;
  }
};

// Get tier-specific styling
const getTierColor = (tier) => {
  const tierValue = typeof tier === 'object' ? tier.value : tier;
  return TIER_COLORS[tierValue] || TIER_COLORS[USER_ACCOUNT_TYPES.BASIC.value];
};

// Get tier icon
const getTierIcon = (tier, props = {}) => {
  const tierValue = typeof tier === 'object' ? tier.value : tier;
  const iconName = TIER_ICONS[tierValue];
  
  switch (iconName) {
    case "Verified":
      return <VerifiedIcon {...props} />;
    case "Star":
      return <StarIcon {...props} />;
    case "Diamond":
      return <DiamondIcon {...props} />;
    default:
      return null;
  }
};

// Styled components
const BenefitItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(0.5, 0),
}));

const CardSection = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  boxShadow: "none",
  border: `1px solid ${theme.palette.divider}`,
}));

const TierCard = styled(Paper)(({ theme, selected, tierColor }) => ({
  backgroundColor: selected ? `${tierColor}0A` : theme.palette.background.paper,
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(2),
  marginBottom: theme.spacing(1),
  position: "relative",
  border: selected
    ? `2px solid ${tierColor}`
    : `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)"}`,
  cursor: "pointer",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    boxShadow: selected ? theme.shadows[4] : theme.shadows[1],
    borderColor: tierColor,
    transform: "translateY(-2px)",
  },
  height: "100%",
  display: "flex",
  flexDirection: "column",
}));

export default function MembershipManagementModal({
  openMembership,
  handleMembershipClose,
  classes,
  userMembershipInfo,
  setFetchedUserData,
}) {
  const {
    appState: { userData },
    createTierCheckoutSession,
    cancelMembershipApiCall,
    reactivateMembershipApiCall,
    toggleAutoRenewApiCall,
    showSnackbar,
  } = useApp();

  // State for tab selection and loading states
  const [tabValue, setTabValue] = useState(0);
  const [selectedTier, setSelectedTier] = useState(null);
  const [loading, setLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState("");

  // Local state for UI updates
  const [isAutoRenew, setIsAutoRenew] = useState(false);
  const [isCanceled, setIsCanceled] = useState(false);

  // Confirmation dialog states
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [showUpgradeConfirmation, setShowUpgradeConfirmation] = useState(false);

  // Subscription plans state
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [plansError, setPlansError] = useState(null);
  const [showReactivateConfirmation, setShowReactivateConfirmation] =
    useState(false);
  const [showAutoRenewConfirmation, setShowAutoRenewConfirmation] =
    useState(false);

  // Get current tier information
  const currentTier = userData?.account_type || USER_ACCOUNT_TYPES.BASIC.value;
  const isBasic = currentTier === USER_ACCOUNT_TYPES.BASIC.value;
  const isMember = !isBasic;

  // Fetch subscription plans from backend
  useEffect(() => {
    const fetchSubscriptionPlans = async () => {
      try {
        setLoadingPlans(true);
        setPlansError(null);

        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/economy/subscription-plans`
        );

        console.log("Subscription plans response:", response.data);

        if (
          response.data &&
          response.data.data &&
          response.data.data.subscription_plans
        ) {
          setSubscriptionPlans(response.data.data.subscription_plans);
          setPlansError(null);
        } else if (response.data && response.data.success) {
          // Response is successful but no plans data
          console.warn("No subscription plans data in response");
          setPlansError("No subscription plans available.");
        } else {
          setPlansError("Failed to load subscription plans.");
        }
      } catch (error) {
        console.error("Error fetching subscription plans:", error);
        if (error.response) {
          // Server responded with error
          setPlansError(`Server error: ${error.response.status}`);
        } else if (error.request) {
          // Request made but no response
          setPlansError("Unable to reach server. Please check your connection.");
        } else {
          // Something else happened
          setPlansError("Failed to load subscription plans. Please try again.");
        }
      } finally {
        setLoadingPlans(false);
      }
    };

    if (openMembership) {
      fetchSubscriptionPlans();
    }
  }, [openMembership]);

  // Sync local state with membership info
  useEffect(() => {
    if (userMembershipInfo) {
      setIsAutoRenew(!!userMembershipInfo.auto_renew);
      setIsCanceled(!!userMembershipInfo.canceled);
    }
  }, [userMembershipInfo]);

  // Format dates
  const membershipEndDate = userMembershipInfo?.end_date
    ? new Date(userMembershipInfo.end_date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  // Next renewal date if applicable
  const nextRenewalDate = userMembershipInfo?.next_renewal_date
    ? new Date(userMembershipInfo.next_renewal_date).toLocaleDateString(
        "en-US",
        {
          year: "numeric",
          month: "long",
          day: "numeric",
        }
      )
    : null;

  // Convert subscription plans from backend to tier structure
  const convertPlansToTiers = () => {
    const tiers = {
      [USER_ACCOUNT_TYPES.BASIC.value]: {
        id: null,
        name: USER_ACCOUNT_TYPES.BASIC.label,
        monthly_cost_in_cents: 0,
        benefits: DEFAULT_TIER_BENEFITS[USER_ACCOUNT_TYPES.BASIC.value],
      },
    };

    subscriptionPlans.forEach((plan) => {
      const tierName = plan.name.toLowerCase();
      tiers[tierName] = {
        id: plan.id,
        name: plan.name,
        monthly_cost_in_cents: plan.monthly_cost_in_cents,
        benefits: plan.benefits || DEFAULT_TIER_BENEFITS[tierName] || [],
      };
    });

    return tiers;
  };

  // Process available tiers from API
  const processAvailableTiers = () => {
    // If we have subscription plans from backend, use those
    if (subscriptionPlans.length > 0) {
      return convertPlansToTiers();
    }

    // Otherwise, try to use userMembershipInfo if available
    if (userMembershipInfo?.available_tiers) {
      const processed = {};
      Object.entries(userMembershipInfo.available_tiers).forEach(
        ([key, value]) => {
          processed[key] = {
            name: value.name || key,
            monthly_cost_in_cents: value.monthly_cost_in_cents || 0,
            benefits: value.benefits || DEFAULT_TIER_BENEFITS[key] || [],
            ...value,
          };
        }
      );

      // Add basic tier if not present
      if (!processed[USER_ACCOUNT_TYPES.BASIC.value]) {
        processed[USER_ACCOUNT_TYPES.BASIC.value] = {
          name: USER_ACCOUNT_TYPES.BASIC.label,
          monthly_cost_in_cents: 0,
          benefits: DEFAULT_TIER_BENEFITS[USER_ACCOUNT_TYPES.BASIC.value],
        };
      }

      return processed;
    }

    // Return empty structure if no data available
    return {
      [USER_ACCOUNT_TYPES.BASIC.value]: {
        name: USER_ACCOUNT_TYPES.BASIC.label,
        monthly_cost_in_cents: 0,
        benefits: DEFAULT_TIER_BENEFITS[USER_ACCOUNT_TYPES.BASIC.value],
      },
    };
  };

  // Process available tiers
  const availableTiers = processAvailableTiers();

  // Current tier benefits
  const currentTierInfo = userMembershipInfo?.current_tier ||
    availableTiers[currentTier] || {
      name: currentTier,
      benefits: DEFAULT_TIER_BENEFITS[currentTier] || [],
    };

  // Format currency for display
  const formatPrice = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  // Set selected tier initially based on current tier
  useEffect(() => {
    // Set tab to current membership if user has one
    if (isMember) {
      setTabValue(0); // "My Membership" tab
    } else {
      setTabValue(1); // "Upgrade" tab
    }
    // Always preselect current tier in Change Tier tab
    setSelectedTier(currentTier);
  }, [isMember, currentTier]);

  // Update time remaining calculation
  useEffect(() => {
    if (userMembershipInfo?.is_active && userMembershipInfo?.end_date) {
      setTimeRemaining(calculateTimeRemaining(userMembershipInfo.end_date));

      // Set up interval to update the time remaining
      const interval = setInterval(() => {
        setTimeRemaining(calculateTimeRemaining(userMembershipInfo.end_date));
      }, 60000); // Update every minute

      return () => clearInterval(interval);
    }
  }, [userMembershipInfo]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle tier selection change
  const handleTierSelect = (tier) => {
    const tierLower = tier.toLowerCase();
    
    // Don't allow selecting current tier when membership is active (not canceled)
    if (tierLower === currentTier.toLowerCase() && !isCanceled) {
      return; // Prevent selection of current tier
    }
    
    // If user is already on basic, don't allow selecting basic as a new tier
    if (tierLower === USER_ACCOUNT_TYPES.BASIC.value && isBasic) {
      return; // Already on basic, no need to select it
    }
    
    setSelectedTier(tier);
  };

  // Confirmation handlers
  const handleUpgradeConfirmationOpen = () => {
    if (!selectedTier) {
      showSnackbar(
        "Please select a membership tier first",
        SNACKBAR_VARIANT.WARNING
      );
      return;
    }
    setShowUpgradeConfirmation(true);
  };

  const handleUpgradeConfirmationClose = () => {
    setShowUpgradeConfirmation(false);
  };

  const handleCancelConfirmationOpen = () => {
    setShowCancelConfirmation(true);
  };

  const handleCancelConfirmationClose = () => {
    setShowCancelConfirmation(false);
  };

  const handleReactivateConfirmationOpen = () => {
    setShowReactivateConfirmation(true);
  };

  const handleReactivateConfirmationClose = () => {
    setShowReactivateConfirmation(false);
  };

  const handleAutoRenewConfirmationOpen = () => {
    setShowAutoRenewConfirmation(true);
  };

  const handleAutoRenewConfirmationClose = () => {
    setShowAutoRenewConfirmation(false);
  };

  // API actions
  const handleChangeTier = async () => {
    if (!selectedTier) return;

    // Don't allow changing to same tier
    if (selectedTier.toLowerCase() === currentTier.toLowerCase() && !isCanceled) {
      showSnackbar(
        "You are already on this tier",
        SNACKBAR_VARIANT.WARNING
      );
      handleUpgradeConfirmationClose();
      return;
    }

    setLoading(true);

    try {
      // Get the plan ID for the selected tier
      const selectedPlan = availableTiers[selectedTier?.toLowerCase()];
      
      if (!selectedPlan?.id) {
        showSnackbar(
          "Selected plan not found. Please try again.",
          SNACKBAR_VARIANT.ERROR
        );
        handleUpgradeConfirmationClose();
        setLoading(false);
        return;
      }

      // Send plan_id to backend
      const response = await createTierCheckoutSession({ plan_id: selectedPlan.id });

      if (response.success) {
        // Closed by redirect to Stripe checkout
        handleUpgradeConfirmationClose();
      } else {
        // Show specific error message from backend
        showSnackbar(
          response.message || "Failed to process tier change. Please try again.",
          SNACKBAR_VARIANT.ERROR
        );
        handleUpgradeConfirmationClose();
      }
    } catch (error) {
      console.error("Error changing tier:", error);
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to process tier change. Please try again.";
      showSnackbar(errorMessage, SNACKBAR_VARIANT.ERROR);
      handleUpgradeConfirmationClose();
    } finally {
      setLoading(false);
    }
  };

  const handleCancelMembership = async () => {
    setLoading(true);

    try {
      const response = await cancelMembershipApiCall();

      if (response.success) {
        handleCancelConfirmationClose();

        // Update both local states for immediate UI update
        setIsCanceled(true);
        setIsAutoRenew(false); // Auto-renewal is always off when cancelled

        // Update fetched data for persistence
        if (userMembershipInfo) {
          setFetchedUserData((prev) => ({
            ...prev,
            userMembershipInfo: {
              ...userMembershipInfo,
              canceled: true,
              auto_renew: false, // Auto-renewal is always off when cancelled
            },
          }));
        }
      }
    } catch (error) {
      console.error("Error cancelling membership:", error);
      showSnackbar(
        "Failed to cancel membership. Please try again.",
        SNACKBAR_VARIANT.ERROR
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReactivateMembership = async () => {
    setLoading(true);

    try {
      const response = await reactivateMembershipApiCall();

      if (response.success) {
        handleReactivateConfirmationClose();

        // Update both local states for immediate UI update
        setIsCanceled(false);
        setIsAutoRenew(true);

        // Update fetched data for persistence
        if (userMembershipInfo) {
          setFetchedUserData((prev) => ({
            ...prev,
            userMembershipInfo: {
              ...userMembershipInfo,
              canceled: false,
              auto_renew: true,
            },
          }));
        }
      }
    } catch (error) {
      console.error("Error reactivating membership:", error);
      showSnackbar(
        "Failed to reactivate membership. Please try again.",
        SNACKBAR_VARIANT.ERROR
      );
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAutoRenew = async () => {
    setLoading(true);

    try {
      // If plan is canceled and trying to turn on auto-renewal, we need to reactivate
      if (isCanceled && !isAutoRenew) {
        // Use reactivate API instead
        const response = await reactivateMembershipApiCall();

        if (response.success) {
          handleAutoRenewConfirmationClose();

          // Update both local states for immediate UI update
          setIsCanceled(false);
          setIsAutoRenew(true);

          // Update fetched data for persistence
          if (userMembershipInfo) {
            setFetchedUserData((prev) => ({
              ...prev,
              userMembershipInfo: {
                ...userMembershipInfo,
                canceled: false,
                auto_renew: true,
              },
            }));
          }
        }
      } else {
        // Normal toggle when not canceled or turning off auto-renewal
        const response = await toggleAutoRenewApiCall();

        if (response.success) {
          handleAutoRenewConfirmationClose();

          // Calculate new state values
          const newAutoRenewValue = !isAutoRenew;

          // Update local states for immediate UI update
          setIsAutoRenew(newAutoRenewValue);

          // Update fetched data for persistence
          if (userMembershipInfo) {
            setFetchedUserData((prev) => ({
              ...prev,
              userMembershipInfo: {
                ...userMembershipInfo,
                auto_renew: newAutoRenewValue,
                // We don't change canceled status when toggling auto-renew off
              },
            }));
          }
        }
      }
    } catch (error) {
      console.error("Error toggling auto-renew:", error);
      showSnackbar(
        "Failed to update auto-renewal setting. Please try again.",
        SNACKBAR_VARIANT.ERROR
      );
    } finally {
      setLoading(false);
    }
  };

  // Feature comparison state for table
  const [featureComparison, setFeatureComparison] = useState([
    // Default hardcoded fallback data
    {
      feature: "Ad Experience",
      basic: "Ads required",
      standard: "Limited ads",
      premium: "No ads",
      elite: "No ads",
    },
    {
      feature: "Maximum Active Rooms",
      basic: "1 room",
      standard: "2 rooms",
      premium: "Unlimited",
      elite: "Unlimited",
    },
    {
      feature: "Room Hosting",
      basic: "None",
      standard: "1 room (3 teams, 12 users)",
      premium: "2 rooms (5 teams, 20 users)",
      elite: "Unlimited rooms",
    },
    {
      feature: "Daily Bonus",
      basic: "0.5x coins",
      standard: "1x coins",
      premium: "2x coins",
      elite: "4x coins",
    },
    {
      feature: "Featured in Trending",
      basic: "No",
      standard: "No",
      premium: "No",
      elite: "Yes",
    },
    {
      feature: "Room Priority",
      basic: "No",
      standard: "No",
      premium: "No",
      elite: "Yes",
    },
  ]);

  // TODO: Fetch feature comparison data from backend
  // Fetch feature comparison from backend API
  useEffect(() => {
    const fetchFeatureComparison = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/economy/feature-comparison`
        );

        if (response.data && response.data.data && response.data.data.features) {
          setFeatureComparison(response.data.data.features);
        }
      } catch (error) {
        console.warn("Feature comparison endpoint not available, using default data:", error);
        // Keep using the default hardcoded data
      }
    };

    if (openMembership) {
      fetchFeatureComparison();
    }
  }, [openMembership]);

  return (
    <>
      <Dialog
        open={openMembership}
        TransitionComponent={Transition}
        onClose={handleMembershipClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Membership Manager
          </Typography>
          <IconButton
            onClick={handleMembershipClose}
            size="small"
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />

        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="membership tabs"
          >
            <Tab
              label="My Membership"
              icon={<WalletIcon />}
              iconPosition="start"
            />
            <Tab
              label="Change Tier"
              icon={<AddCircleOutlineIcon />}
              iconPosition="start"
            />
            <Tab
              label="Compare Plans"
              icon={<CompareIcon />}
              iconPosition="start"
            />
          </Tabs>
        </Box>

        <DialogContent sx={{ py: 2, minHeight: 400 }}>
          {/* Loading State */}
          {loadingPlans && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 8,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {/* Error State */}
          {!loadingPlans && plansError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setPlansError(null);
                    setLoadingPlans(true);
                    // Trigger refetch by toggling the modal state
                    const fetchPlans = async () => {
                      try {
                        setLoadingPlans(true);
                        setPlansError(null);
                        const response = await axios.get(
                          `${process.env.REACT_APP_API_BASE_URL}/economy/subscription-plans`
                        );
                        if (response.data?.data?.subscription_plans) {
                          setSubscriptionPlans(response.data.data.subscription_plans);
                        } else {
                          setPlansError("No subscription plans available.");
                        }
                      } catch (error) {
                        console.error("Retry failed:", error);
                        setPlansError("Failed to load subscription plans. Please try again.");
                      } finally {
                        setLoadingPlans(false);
                      }
                    };
                    fetchPlans();
                  }}
                >
                  Retry
                </Button>
              }
            >
              {plansError}
            </Alert>
          )}

          {/* Content - Only show when not loading */}
          {!loadingPlans && !plansError && (
            <>
              {/* My Membership Tab */}
              {tabValue === 0 && (
                <Fade in={tabValue === 0} timeout={400}>
            <Box>
              {!isMember ? (
                <Box sx={{ textAlign: "center", py: 6 }}>
                  <Typography variant="h6" sx={{ mb: 2, color: "text.secondary" }}>
                    You don't have an active membership
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
                    Upgrade to unlock exclusive features and benefits
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => setTabValue(1)}
                    size="large"
                    sx={{
                      bgcolor: getTierColor("premium"),
                      "&:hover": {
                        bgcolor: getTierColor("premium"),
                        opacity: 0.9,
                      },
                    }}
                  >
                    View Membership Tiers
                  </Button>
                </Box>
              ) : (
                <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                  mt: 1,
                }}
              >
                {getTierIcon(currentTier, {
                  sx: {
                    color: getTierColor(currentTier),
                    mr: 1.5,
                    fontSize: "1.75rem",
                    verticalAlign: "middle",
                  },
                })}
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: isCanceled ? "text.secondary" : "text.primary",
                    textTransform: "capitalize",
                  }}
                >
                  {currentTier} {isCanceled ? "(Canceled)" : "Member"}
                </Typography>
              </Box>

              {isCanceled ? (
                <CardSection
                  elevation={0}
                  sx={(theme) => ({
                    mb: 2,
                    bgcolor: theme.palette.mode === "dark"
                      ? "rgba(236, 151, 31, 0.25)"
                      : "rgba(236, 151, 31, 0.15)",
                    borderColor: theme.palette.mode === "dark"
                      ? "rgb(236, 151, 31)"
                      : "rgb(215, 138, 28)",
                    borderLeft: theme.palette.mode === "dark"
                      ? "4px solid rgb(236, 151, 31)"
                      : "4px solid rgb(215, 138, 28)",
                  })}
                >
                  <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                    <ErrorIcon
                      sx={(theme) => ({
                        color: theme.palette.mode === "dark"
                          ? "rgb(255, 183, 77)"
                          : "rgb(215, 138, 28)",
                        mr: 1.5,
                        mt: 0.5
                      })}
                    />
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={(theme) => ({
                          color: theme.palette.mode === "dark"
                            ? "rgb(255, 213, 79)"
                            : "rgb(176, 91, 12)",
                          fontWeight: 600
                        })}
                      >
                        Your membership is canceled
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={(theme) => ({
                          color: theme.palette.mode === "dark"
                            ? "rgb(255, 224, 130)"
                            : "rgb(71, 58, 38)",
                          mt: 0.5
                        })}
                      >
                        Benefits remain active until {membershipEndDate}
                      </Typography>
                      {timeRemaining && (
                        <Typography
                          variant="body2"
                          sx={(theme) => ({
                            color: theme.palette.mode === "dark"
                              ? "rgb(255, 193, 7)"
                              : "rgb(176, 91, 12)",
                            fontWeight: 600,
                            mt: 0.5,
                            display: "flex",
                            alignItems: "center",
                          })}
                        >
                          <CalendarIcon
                            sx={{ fontSize: "0.875rem", mr: 0.5 }}
                          />
                          Time remaining: {timeRemaining}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </CardSection>
              ) : (
                <CardSection
                  elevation={0}
                  sx={{
                    mb: 2,
                    bgcolor: "rgba(25, 118, 210, 0.08)",
                    borderColor: getTierColor(currentTier),
                    borderLeft: `4px solid ${getTierColor(currentTier)}`,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {isAutoRenew ? (
                      <AutorenewIcon
                        sx={{ color: getTierColor(currentTier), mr: 1 }}
                      />
                    ) : (
                      <CalendarIcon
                        sx={{ color: getTierColor(currentTier), mr: 1 }}
                      />
                    )}
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: getTierColor(currentTier),
                          fontWeight: 500,
                        }}
                      >
                        Active until {membershipEndDate}
                      </Typography>
                      {isAutoRenew && nextRenewalDate ? (
                        <Typography variant="body2" color="text.secondary">
                          Will renew on {nextRenewalDate}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Auto-renewal is disabled
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </CardSection>
              )}

              <CardSection elevation={0}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Your Benefits
                </Typography>
                <List disablePadding>
                  {currentTierInfo?.benefits?.map((benefit, index) => (
                    <BenefitItem key={index} disableGutters>
                      <CheckCircleIcon
                        fontSize="small"
                        sx={{
                          color: getTierColor(currentTier),
                          mr: 1.5,
                          fontSize: "1.125rem",
                          opacity: isCanceled ? 0.8 : 1,
                        }}
                      />
                      <ListItemText
                        primary={benefit}
                        primaryTypographyProps={{
                          variant: "body2",
                          color: isCanceled ? "text.secondary" : "text.primary",
                          fontWeight: isCanceled ? "normal" : "medium",
                        }}
                      />
                    </BenefitItem>
                  ))}
                </List>
              </CardSection>

              {/* Membership Actions */}
              <Box
                sx={{
                  mt: 3,
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                {/* Only show auto-renewal toggle when plan is NOT canceled */}
                {!isCanceled && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isAutoRenew}
                        onChange={handleAutoRenewConfirmationOpen}
                        disabled={loading}
                      />
                    }
                    label={`Auto-renewal is ${
                      isAutoRenew ? "enabled" : "disabled"
                    }`}
                    sx={{ mb: 2 }}
                  />
                )}

                {/* Show reactivate button when canceled, otherwise show cancel/change buttons */}
                {isCanceled ? (
                  <Button
                    variant="contained"
                    onClick={handleReactivateConfirmationOpen}
                    size="medium"
                    disabled={loading}
                    sx={{
                      minWidth: 200,
                      bgcolor: getTierColor(currentTier),
                      "&:hover": {
                        bgcolor: getTierColor(currentTier),
                        opacity: 0.9,
                      },
                      fontWeight: 500,
                      py: 1,
                    }}
                    startIcon={
                      loading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : null
                    }
                  >
                    REACTIVATE MEMBERSHIP
                  </Button>
                ) : (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", gap: 2 }}
                  >
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleCancelConfirmationOpen}
                      size="medium"
                      disabled={loading}
                      startIcon={
                        loading ? (
                          <CircularProgress size={20} color="error" />
                        ) : null
                      }
                    >
                      Cancel Membership
                    </Button>

                    <Button
                      variant="contained"
                      onClick={() => {
                        setTabValue(1); // Switch to change tier tab
                        // Keep current tier selected
                      }}
                      size="medium"
                      sx={{
                        bgcolor: getTierColor("premium"),
                        "&:hover": {
                          bgcolor: getTierColor("premium"),
                          opacity: 0.9,
                        },
                      }}
                      startIcon={<AddCircleOutlineIcon />}
                    >
                      Change Tier
                    </Button>
                  </Box>
                )}
              </Box>
              </>
              )}
            </Box>
                </Fade>
          )}

          {/* Change Tier Tab */}
          {tabValue === 1 && (
            <Fade in={tabValue === 1} timeout={400}>
            <Box>
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, color: "text.primary" }}>
                  Choose your membership tier
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", opacity: 0.9 }}>
                  You can upgrade or downgrade your membership at any time
                </Typography>
              </Box>

              {/* For larger screens - grid layout */}
              <Box sx={{ display: { xs: "none", md: "block" } }}>
                <Grid container spacing={2} mb={4}>
                  {/* Basic Tier */}
                  <Grid item md={3}>
                    <TierCard
                      selected={selectedTier?.toLowerCase() === USER_ACCOUNT_TYPES.BASIC.value}
                      tierColor={getTierColor(USER_ACCOUNT_TYPES.BASIC.value)}
                      onClick={() => handleTierSelect(USER_ACCOUNT_TYPES.BASIC.value)}
                      sx={(theme) => ({
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        p: 2,
                        opacity: currentTier === USER_ACCOUNT_TYPES.BASIC.value && !isCanceled ? 0.7 : 1,
                        backgroundColor:
                          selectedTier?.toLowerCase() === USER_ACCOUNT_TYPES.BASIC.value
                            ? `${getTierColor(USER_ACCOUNT_TYPES.BASIC.value)}0A`
                            : theme.palette.background.paper,
                        border:
                          selectedTier?.toLowerCase() === USER_ACCOUNT_TYPES.BASIC.value
                            ? `2px solid ${getTierColor(USER_ACCOUNT_TYPES.BASIC.value)}`
                            : theme.palette.mode === "dark"
                            ? "1px solid rgba(255, 255, 255, 0.12)"
                            : "1px solid rgba(0, 0, 0, 0.12)",
                        borderRadius: 2,
                        cursor: currentTier === USER_ACCOUNT_TYPES.BASIC.value && !isCanceled ? "not-allowed" : "pointer",
                        pointerEvents: currentTier === USER_ACCOUNT_TYPES.BASIC.value && !isCanceled ? "none" : "auto",
                      })}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: getTierColor(USER_ACCOUNT_TYPES.BASIC.value),
                            fontSize: "1.25rem",
                          }}
                        >
                          Basic
                        </Typography>
                      </Box>

                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 700, mb: 2, fontSize: "1.5rem" }}
                      >
                        Free
                      </Typography>

                      <Divider sx={{ mb: 2 }} />

                      <List
                        disablePadding
                        sx={{
                          flexGrow: 1,
                          "& .MuiListItem-root": {
                            py: 0.5,
                          },
                        }}
                      >
                        {availableTiers[USER_ACCOUNT_TYPES.BASIC.value]?.benefits?.map(
                          (benefit, index) => (
                            <BenefitItem key={index} disableGutters>
                              <CheckCircleIcon
                                fontSize="small"
                                sx={{
                                  color: getTierColor(USER_ACCOUNT_TYPES.BASIC.value),
                                  mr: 1.5,
                                  fontSize: "1rem",
                                  flexShrink: 0,
                                }}
                              />
                              <ListItemText
                                primary={benefit}
                                primaryTypographyProps={{
                                  variant: "body2",
                                  fontSize: "0.875rem",
                                }}
                              />
                            </BenefitItem>
                          )
                        )}
                      </List>

                      {currentTier === USER_ACCOUNT_TYPES.BASIC.value && !isCanceled ? (
                        <Chip
                          label="Current Plan"
                          sx={{
                            mt: 2,
                            bgcolor: getTierColor(USER_ACCOUNT_TYPES.BASIC.value),
                            color: "white",
                          }}
                          size="small"
                        />
                      ) : null}
                    </TierCard>
                  </Grid>

                  {/* Standard Tier */}
                  <Grid item md={3}>
                    <TierCard
                      selected={selectedTier?.toLowerCase() === "standard"}
                      tierColor={getTierColor("standard")}
                      onClick={() => handleTierSelect("standard")}
                      sx={(theme) => ({
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        p: 2,
                        opacity: currentTier === "standard" && !isCanceled ? 0.7 : 1,
                        backgroundColor:
                          selectedTier === "standard"
                            ? `${getTierColor("standard")}0A`
                            : theme.palette.background.paper,
                        border:
                          selectedTier === "standard"
                            ? `2px solid ${getTierColor("standard")}`
                            : theme.palette.mode === "dark"
                            ? "1px solid rgba(255, 255, 255, 0.12)"
                            : "1px solid rgba(0, 0, 0, 0.12)",
                        borderRadius: 2,
                        cursor: currentTier === "standard" && !isCanceled ? "not-allowed" : "pointer",
                        pointerEvents: currentTier === "standard" && !isCanceled ? "none" : "auto",
                      })}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: getTierColor("standard"),
                            fontSize: "1.25rem",
                          }}
                        >
                          Standard
                        </Typography>
                        {getTierIcon("standard", {
                          sx: {
                            color: getTierColor("standard"),
                            fontSize: "1.5rem",
                          },
                        })}
                      </Box>

                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 700, mb: 2, fontSize: "1.5rem" }}
                      >
                        {formatPrice(
                          availableTiers.standard?.monthly_cost_in_cents ||
                            0
                        )}
                        <span style={{ fontSize: "0.8rem", fontWeight: 400 }}>
                          /month
                        </span>
                      </Typography>

                      <Divider sx={{ mb: 2 }} />

                      <List
                        disablePadding
                        sx={{
                          flexGrow: 1,
                          "& .MuiListItem-root": {
                            py: 0.5,
                          },
                        }}
                      >
                        {availableTiers.standard?.benefits?.map(
                          (benefit, index) => (
                            <BenefitItem key={index} disableGutters>
                              <CheckCircleIcon
                                fontSize="small"
                                sx={{
                                  color: getTierColor("standard"),
                                  mr: 1.5,
                                  fontSize: "1rem",
                                  flexShrink: 0,
                                }}
                              />
                              <ListItemText
                                primary={benefit}
                                primaryTypographyProps={{
                                  variant: "body2",
                                  fontSize: "0.875rem",
                                }}
                              />
                            </BenefitItem>
                          )
                        )}
                      </List>

                      {currentTier === "standard" && !isCanceled ? (
                        <Chip
                          label="Current Plan"
                          sx={{
                            mt: 2,
                            bgcolor: getTierColor("standard"),
                            color: "white",
                          }}
                          size="small"
                        />
                      ) : null}
                    </TierCard>
                  </Grid>

                  {/* Premium Tier */}
                  <Grid item md={3}>
                    <TierCard
                      selected={selectedTier?.toLowerCase() === "premium"}
                      tierColor={getTierColor("premium")}
                      onClick={() => handleTierSelect("premium")}
                      sx={(theme) => ({
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        position: "relative",
                        p: 2,
                        opacity: currentTier === "premium" && !isCanceled ? 0.7 : 1,
                        backgroundColor:
                          selectedTier === "premium"
                            ? `${getTierColor("premium")}0A`
                            : theme.palette.background.paper,
                        border:
                          selectedTier === "premium"
                            ? `2px solid ${getTierColor("premium")}`
                            : theme.palette.mode === "dark"
                            ? "1px solid rgba(255, 255, 255, 0.12)"
                            : "1px solid rgba(0, 0, 0, 0.12)",
                        borderRadius: 2,
                        cursor: currentTier === "premium" && !isCanceled ? "not-allowed" : "pointer",
                        pointerEvents: currentTier === "premium" && !isCanceled ? "none" : "auto",
                      })}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          top: -12,
                          left: "50%",
                          transform: "translateX(-50%)",
                          bgcolor: getTierColor("premium"),
                          color: "white",
                          px: 2,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          zIndex: 1,
                        }}
                      >
                        MOST POPULAR
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: getTierColor("premium"),
                            fontSize: "1.25rem",
                          }}
                        >
                          Premium
                        </Typography>
                        {getTierIcon("premium", {
                          sx: {
                            color: getTierColor("premium"),
                            fontSize: "1.5rem",
                          },
                        })}
                      </Box>

                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 700, mb: 2, fontSize: "1.5rem" }}
                      >
                        {formatPrice(
                          availableTiers.premium?.monthly_cost_in_cents ||
                            0
                        )}
                        <span style={{ fontSize: "0.8rem", fontWeight: 400 }}>
                          /month
                        </span>
                      </Typography>

                      <Divider sx={{ mb: 2 }} />

                      <List
                        disablePadding
                        sx={{
                          flexGrow: 1,
                          "& .MuiListItem-root": {
                            py: 0.5,
                          },
                        }}
                      >
                        {availableTiers.premium?.benefits?.map(
                          (benefit, index) => (
                            <BenefitItem key={index} disableGutters>
                              <CheckCircleIcon
                                fontSize="small"
                                sx={{
                                  color: getTierColor("premium"),
                                  mr: 1.5,
                                  fontSize: "1rem",
                                  flexShrink: 0,
                                }}
                              />
                              <ListItemText
                                primary={benefit}
                                primaryTypographyProps={{
                                  variant: "body2",
                                  fontSize: "0.875rem",
                                }}
                              />
                            </BenefitItem>
                          )
                        )}
                      </List>
                      {currentTier === "premium" && !isCanceled ? (
                        <Chip
                          label="Current Plan"
                          sx={{
                            mt: 2,
                            bgcolor: getTierColor("premium"),
                            color: "white",
                          }}
                          size="small"
                        />
                      ) : null}
                    </TierCard>
                  </Grid>

                  {/* Elite Tier */}
                  <Grid item md={3}>
                    <TierCard
                      selected={selectedTier?.toLowerCase() === "elite"}
                      tierColor={getTierColor("elite")}
                      onClick={() => handleTierSelect("elite")}
                      sx={(theme) => ({
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        p: 2,
                        opacity: currentTier === "elite" && !isCanceled ? 0.7 : 1,
                        backgroundColor:
                          selectedTier === "elite"
                            ? `${getTierColor("elite")}0A`
                            : theme.palette.background.paper,
                        border:
                          selectedTier === "elite"
                            ? `2px solid ${getTierColor("elite")}`
                            : theme.palette.mode === "dark"
                            ? "1px solid rgba(255, 255, 255, 0.12)"
                            : "1px solid rgba(0, 0, 0, 0.12)",
                        borderRadius: 2,
                        cursor: currentTier === "elite" && !isCanceled ? "not-allowed" : "pointer",
                        pointerEvents: currentTier === "elite" && !isCanceled ? "none" : "auto",
                      })}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: getTierColor("elite"),
                            fontSize: "1.25rem",
                          }}
                        >
                          Elite
                        </Typography>
                        {getTierIcon("elite", {
                          sx: {
                            color: getTierColor("elite"),
                            fontSize: "1.5rem",
                          },
                        })}
                      </Box>

                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 700, mb: 2, fontSize: "1.5rem" }}
                      >
                        {formatPrice(
                          availableTiers.elite?.monthly_cost_in_cents ||
                            0
                        )}
                        <span style={{ fontSize: "0.8rem", fontWeight: 400 }}>
                          /month
                        </span>
                      </Typography>

                      <Divider sx={{ mb: 2 }} />

                      <List
                        disablePadding
                        sx={{
                          flexGrow: 1,
                          "& .MuiListItem-root": {
                            py: 0.5,
                          },
                        }}
                      >
                        {availableTiers.elite?.benefits?.map(
                          (benefit, index) => (
                            <BenefitItem key={index} disableGutters>
                              <CheckCircleIcon
                                fontSize="small"
                                sx={{
                                  color: getTierColor("elite"),
                                  mr: 1.5,
                                  fontSize: "1rem",
                                  flexShrink: 0,
                                }}
                              />
                              <ListItemText
                                primary={benefit}
                                primaryTypographyProps={{
                                  variant: "body2",
                                  fontSize: "0.875rem",
                                }}
                              />
                            </BenefitItem>
                          )
                        )}
                      </List>

                      {currentTier === "elite" && !isCanceled ? (
                        <Chip
                          label="Current Plan"
                          sx={{
                            mt: 2,
                            bgcolor: getTierColor("elite"),
                            color: "white",
                          }}
                          size="small"
                        />
                      ) : null}
                    </TierCard>
                  </Grid>
                </Grid>
              </Box>

              {/* For mobile screens - stacked layout */}
              <Box
                sx={{
                  display: { xs: "flex", md: "none" },
                  flexDirection: "column",
                  gap: 4,
                  mb: 4,
                }}
              >
                {/* Basic Tier */}
                <TierCard
                  selected={selectedTier?.toLowerCase() === USER_ACCOUNT_TYPES.BASIC.value}
                  tierColor={getTierColor(USER_ACCOUNT_TYPES.BASIC.value)}
                  onClick={() => handleTierSelect(USER_ACCOUNT_TYPES.BASIC.value)}
                  sx={(theme) => ({
                    display: "flex",
                    flexDirection: "column",
                    p: 3,
                    mx: 2,
                    opacity: currentTier === USER_ACCOUNT_TYPES.BASIC.value && !isCanceled ? 0.7 : 1,
                    backgroundColor:
                      selectedTier?.toLowerCase() === USER_ACCOUNT_TYPES.BASIC.value
                        ? `${getTierColor(USER_ACCOUNT_TYPES.BASIC.value)}0A`
                        : theme.palette.background.paper,
                    border:
                      selectedTier?.toLowerCase() === USER_ACCOUNT_TYPES.BASIC.value
                        ? `2px solid ${getTierColor(USER_ACCOUNT_TYPES.BASIC.value)}`
                        : theme.palette.mode === "dark"
                        ? "1px solid rgba(255, 255, 255, 0.12)"
                        : "1px solid rgba(0, 0, 0, 0.12)",
                    borderRadius: 2,
                    cursor: currentTier === USER_ACCOUNT_TYPES.BASIC.value && !isCanceled ? "not-allowed" : "pointer",
                    pointerEvents: currentTier === USER_ACCOUNT_TYPES.BASIC.value && !isCanceled ? "none" : "auto",
                  })}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: getTierColor(USER_ACCOUNT_TYPES.BASIC.value),
                        fontSize: "1.3rem",
                      }}
                    >
                      Basic
                    </Typography>
                  </Box>

                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, mb: 2, fontSize: "1.75rem" }}
                  >
                    Free
                  </Typography>

                  <Divider sx={{ mb: 2 }} />

                  <List
                    disablePadding
                    sx={{
                      flexGrow: 1,
                      "& .MuiListItem-root": {
                        py: 1,
                      },
                    }}
                  >
                    {availableTiers[USER_ACCOUNT_TYPES.BASIC.value]?.benefits?.map(
                      (benefit, index) => (
                        <BenefitItem key={index} disableGutters>
                          <CheckCircleIcon
                            fontSize="small"
                            sx={{
                              color: getTierColor(USER_ACCOUNT_TYPES.BASIC.value),
                              mr: 1.5,
                              fontSize: "1.2rem",
                              flexShrink: 0,
                            }}
                          />
                          <ListItemText
                            primary={benefit}
                            primaryTypographyProps={{
                              variant: "body2",
                              fontSize: "1rem",
                            }}
                          />
                        </BenefitItem>
                      )
                    )}
                  </List>

                  {currentTier === USER_ACCOUNT_TYPES.BASIC.value && !isCanceled ? (
                    <Chip
                      label="Current Plan"
                      sx={{
                        mt: 2,
                        bgcolor: getTierColor(USER_ACCOUNT_TYPES.BASIC.value),
                        color: "white",
                      }}
                      size="small"
                    />
                  ) : null}
                </TierCard>

                {/* Standard Tier */}
                <TierCard
                  selected={selectedTier?.toLowerCase() === "standard"}
                  tierColor={getTierColor("standard")}
                  onClick={() => handleTierSelect("standard")}
                  sx={(theme) => ({
                    display: "flex",
                    flexDirection: "column",
                    p: 3,
                    mx: 2,
                    opacity: currentTier === "standard" && !isCanceled ? 0.7 : 1,
                    backgroundColor:
                      selectedTier === "standard"
                        ? `${getTierColor("standard")}0A`
                        : theme.palette.background.paper,
                    border:
                      selectedTier === "standard"
                        ? `2px solid ${getTierColor("standard")}`
                        : theme.palette.mode === "dark"
                        ? "1px solid rgba(255, 255, 255, 0.12)"
                        : "1px solid rgba(0, 0, 0, 0.12)",
                    borderRadius: 2,
                    cursor: currentTier === "standard" && !isCanceled ? "not-allowed" : "pointer",
                    pointerEvents: currentTier === "standard" && !isCanceled ? "none" : "auto",
                  })}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: getTierColor("standard"),
                        fontSize: "1.3rem",
                      }}
                    >
                      Standard
                    </Typography>
                    {getTierIcon("standard", {
                      sx: {
                        color: getTierColor("standard"),
                        fontSize: "1.75rem",
                      },
                    })}
                  </Box>

                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, mb: 2, fontSize: "1.75rem" }}
                  >
                    {formatPrice(
                      availableTiers.standard?.monthly_cost_in_cents ||
                        0
                    )}
                    <span style={{ fontSize: "0.8rem", fontWeight: 400 }}>
                      /month
                    </span>
                  </Typography>

                  <Divider sx={{ mb: 2 }} />

                  <List
                    disablePadding
                    sx={{
                      flexGrow: 1,
                      "& .MuiListItem-root": {
                        py: 1,
                      },
                    }}
                  >
                    {availableTiers.standard?.benefits?.map(
                      (benefit, index) => (
                        <BenefitItem key={index} disableGutters>
                          <CheckCircleIcon
                            fontSize="small"
                            sx={{
                              color: getTierColor("standard"),
                              mr: 1.5,
                              fontSize: "1.2rem",
                              flexShrink: 0,
                            }}
                          />
                          <ListItemText
                            primary={benefit}
                            primaryTypographyProps={{
                              variant: "body2",
                              fontSize: "1rem",
                            }}
                          />
                        </BenefitItem>
                      )
                    )}
                  </List>

                  {currentTier === "standard" && !isCanceled ? (
                    <Chip
                      label="Current Plan"
                      sx={{
                        mt: 2,
                        bgcolor: getTierColor("standard"),
                        color: "white",
                      }}
                      size="small"
                    />
                  ) : null}
                </TierCard>

                {/* Premium Tier */}
                <TierCard
                  selected={selectedTier?.toLowerCase() === "premium"}
                  tierColor={getTierColor("premium")}
                  onClick={() => handleTierSelect("premium")}
                  sx={(theme) => ({
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    p: 3,
                    mx: 2,
                    opacity: currentTier === "premium" && !isCanceled ? 0.7 : 1,
                    backgroundColor:
                      selectedTier === "premium"
                        ? `${getTierColor("premium")}0A`
                        : theme.palette.background.paper,
                    border:
                      selectedTier === "premium"
                        ? `2px solid ${getTierColor("premium")}`
                        : theme.palette.mode === "dark"
                        ? "1px solid rgba(255, 255, 255, 0.12)"
                        : "1px solid rgba(0, 0, 0, 0.12)",
                    borderRadius: 2,
                    cursor: currentTier === "premium" && !isCanceled ? "not-allowed" : "pointer",
                    pointerEvents: currentTier === "premium" && !isCanceled ? "none" : "auto",
                  })}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: -12,
                      left: "50%",
                      transform: "translateX(-50%)",
                      bgcolor: getTierColor("premium"),
                      color: "white",
                      px: 2,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      zIndex: 1,
                    }}
                  >
                    MOST POPULAR
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: getTierColor("premium"),
                        fontSize: "1.3rem",
                      }}
                    >
                      Premium
                    </Typography>
                    {getTierIcon("premium", {
                      sx: {
                        color: getTierColor("premium"),
                        fontSize: "1.75rem",
                      },
                    })}
                  </Box>

                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, mb: 2, fontSize: "1.75rem" }}
                  >
                    {formatPrice(
                      availableTiers.premium?.monthly_cost_in_cents ||
                        0
                    )}
                    <span style={{ fontSize: "0.8rem", fontWeight: 400 }}>
                      /month
                    </span>
                  </Typography>

                  <Divider sx={{ mb: 2 }} />

                  <List
                    disablePadding
                    sx={{
                      flexGrow: 1,
                      "& .MuiListItem-root": {
                        py: 1,
                      },
                    }}
                  >
                    {availableTiers.premium?.benefits?.map((benefit, index) => (
                      <BenefitItem key={index} disableGutters>
                        <CheckCircleIcon
                          fontSize="small"
                          sx={{
                            color: getTierColor("premium"),
                            mr: 1.5,
                            fontSize: "1.2rem",
                            flexShrink: 0,
                          }}
                        />
                        <ListItemText
                          primary={benefit}
                          primaryTypographyProps={{
                            variant: "body2",
                            fontSize: "1rem",
                          }}
                        />
                      </BenefitItem>
                    ))}
                  </List>
                  {currentTier === "premium" && !isCanceled ? (
                    <Chip
                      label="Current Plan"
                      sx={{
                        mt: 2,
                        bgcolor: getTierColor("premium"),
                        color: "white",
                      }}
                      size="small"
                    />
                  ) : null}
                </TierCard>

                {/* Elite Tier */}
                <TierCard
                  selected={selectedTier?.toLowerCase() === "elite"}
                  tierColor={getTierColor("elite")}
                  onClick={() => handleTierSelect("elite")}
                  sx={(theme) => ({
                    display: "flex",
                    flexDirection: "column",
                    p: 3,
                    mx: 2,
                    opacity: currentTier === "elite" && !isCanceled ? 0.7 : 1,
                    backgroundColor:
                      selectedTier === "elite"
                        ? `${getTierColor("elite")}0A`
                        : theme.palette.background.paper,
                    border:
                      selectedTier === "elite"
                        ? `2px solid ${getTierColor("elite")}`
                        : theme.palette.mode === "dark"
                        ? "1px solid rgba(255, 255, 255, 0.12)"
                        : "1px solid rgba(0, 0, 0, 0.12)",
                    borderRadius: 2,
                    cursor: currentTier === "elite" && !isCanceled ? "not-allowed" : "pointer",
                    pointerEvents: currentTier === "elite" && !isCanceled ? "none" : "auto",
                  })}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: getTierColor("elite"),
                        fontSize: "1.3rem",
                      }}
                    >
                      Elite
                    </Typography>
                    {getTierIcon("elite", {
                      sx: {
                        color: getTierColor("elite"),
                        fontSize: "1.75rem",
                      },
                    })}
                  </Box>

                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, mb: 2, fontSize: "1.75rem" }}
                  >
                    {formatPrice(
                      availableTiers.elite?.monthly_cost_in_cents ||
                        0
                    )}
                    <span style={{ fontSize: "0.8rem", fontWeight: 400 }}>
                      /month
                    </span>
                  </Typography>

                  <Divider sx={{ mb: 2 }} />

                  <List
                    disablePadding
                    sx={{
                      flexGrow: 1,
                      "& .MuiListItem-root": {
                        py: 1,
                      },
                    }}
                  >
                    {availableTiers.elite?.benefits?.map((benefit, index) => (
                      <BenefitItem key={index} disableGutters>
                        <CheckCircleIcon
                          fontSize="small"
                          sx={{
                            color: getTierColor("elite"),
                            mr: 1.5,
                            fontSize: "1.2rem",
                            flexShrink: 0,
                          }}
                        />
                        <ListItemText
                          primary={benefit}
                          primaryTypographyProps={{
                            variant: "body2",
                            fontSize: "1rem",
                          }}
                        />
                      </BenefitItem>
                    ))}
                  </List>

                  {currentTier === "elite" && !isCanceled ? (
                    <Chip
                      label="Current Plan"
                      sx={{
                        mt: 2,
                        bgcolor: getTierColor("elite"),
                        color: "white",
                      }}
                      size="small"
                    />
                  ) : null}
                </TierCard>
              </Box>

              {/* Change Tier Action */}
              <Box
                sx={{
                  mt: { xs: 4, md: 6 },
                  textAlign: "center",
                  mb: 2,
                  position: "relative",
                  zIndex: 2,
                }}
              >
                <Button
                  variant={selectedTier?.toLowerCase() === currentTier?.toLowerCase() && !isCanceled ? "outlined" : "contained"}
                  onClick={selectedTier?.toLowerCase() === currentTier?.toLowerCase() && !isCanceled ? undefined : handleUpgradeConfirmationOpen}
                  size="large"
                  disabled={!selectedTier || loading || (selectedTier?.toLowerCase() === currentTier?.toLowerCase() && !isCanceled) || (selectedTier?.toLowerCase() === USER_ACCOUNT_TYPES.BASIC.value && isBasic)}
                  sx={(theme) => ({
                    minWidth: { xs: "80%", sm: 200 },
                    py: { xs: 1.5, md: 1 },
                    ...(selectedTier?.toLowerCase() === currentTier?.toLowerCase() && !isCanceled || (selectedTier?.toLowerCase() === USER_ACCOUNT_TYPES.BASIC.value && isBasic)
                      ? {
                          // Disabled current tier styling
                          opacity: 0.5,
                          borderColor: theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.23)"
                            : "rgba(0, 0, 0, 0.23)",
                          backgroundColor: theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.05)"
                            : "rgba(0, 0, 0, 0.02)",
                          color: theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.4)"
                            : "rgba(0, 0, 0, 0.4)",
                          cursor: "not-allowed",
                          pointerEvents: "none",
                        }
                      : {
                          // Active button styling
                          bgcolor: selectedTier
                            ? selectedTier.toLowerCase() === USER_ACCOUNT_TYPES.BASIC.value
                              ? TIER_COLORS[USER_ACCOUNT_TYPES.BASIC.value]
                              : getTierColor(selectedTier)
                            : theme.palette.action.disabledBackground,
                          color: "white",
                          "&:hover": {
                            bgcolor: selectedTier
                              ? selectedTier.toLowerCase() === USER_ACCOUNT_TYPES.BASIC.value
                                ? TIER_COLORS[USER_ACCOUNT_TYPES.BASIC.value]
                                : getTierColor(selectedTier)
                              : theme.palette.action.disabledBackground,
                            opacity: 0.9,
                          },
                        }),
                  })}
                  startIcon={
                    loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : null
                  }
                >
                  {!selectedTier
                    ? "Select a Tier"
                    : selectedTier?.toLowerCase() === currentTier?.toLowerCase() && !isCanceled
                    ? "Current Plan"
                    : selectedTier?.toLowerCase() === USER_ACCOUNT_TYPES.BASIC.value
                    ? isBasic
                      ? "Already Basic"
                      : "Downgrade to Basic"
                    : `Switch to ${
                        selectedTier.charAt(0).toUpperCase() +
                        selectedTier.slice(1)
                      }`}
                </Button>

                <Typography
                  variant="caption"
                  sx={(theme) => ({
                    display: "block",
                    mt: 2,
                    color: theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.6)"
                      : "rgba(0, 0, 0, 0.6)",
                    fontSize: { xs: "0.85rem" },
                  })}
                >
                  {!selectedTier
                    ? "Please select a membership tier"
                    : selectedTier?.toLowerCase() === currentTier?.toLowerCase() && !isCanceled
                    ? "This is your current tier"
                    : selectedTier?.toLowerCase() === USER_ACCOUNT_TYPES.BASIC.value && isBasic
                    ? "You are already on the Basic tier"
                    : selectedTier?.toLowerCase() === USER_ACCOUNT_TYPES.BASIC.value
                    ? "Cancel your membership to downgrade to Basic"
                    : "You will be redirected to complete payment"}
                </Typography>
              </Box>
            </Box>
            </Fade>
          )}

          {/* Compare Plans Tab */}
          {tabValue === 2 && (
            <Fade in={tabValue === 2} timeout={400}>
            <Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1, color: "text.primary", fontWeight: 500 }}>
                  Plan Comparison
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", opacity: 0.9 }}>
                  See which features are available with each membership tier
                </Typography>
              </Box>

              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table
                  sx={{ minWidth: 650 }}
                  aria-label="plan comparison table"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Feature</TableCell>
                      <TableCell align="center">
                        <Typography color="textSecondary">Basic</Typography>
                        <Typography variant="caption" color="textSecondary">
                          Free
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography color={getTierColor("standard")}>
                          Standard
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {formatPrice(
                            availableTiers.standard?.monthly_cost_in_cents ||
                              0
                          )}
                          /mo
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography color={getTierColor("premium")}>
                          Premium
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {formatPrice(
                            availableTiers.premium?.monthly_cost_in_cents ||
                              0
                          )}
                          /mo
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography color={getTierColor("elite")}>
                          Elite
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {formatPrice(
                            availableTiers.elite?.monthly_cost_in_cents ||
                              0
                          )}
                          /mo
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {featureComparison.map((feature, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell component="th" scope="row">
                          {feature.feature}
                        </TableCell>
                        <TableCell align="center">
                          {feature.basic === "Yes" ? (
                            <CheckCircleIcon color="success" fontSize="small" />
                          ) : feature.basic === "No" ? (
                            <BlockIcon color="disabled" fontSize="small" />
                          ) : (
                            feature.basic
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {feature.standard === "Yes" ? (
                            <CheckCircleIcon
                              sx={{ color: getTierColor("standard") }}
                              fontSize="small"
                            />
                          ) : feature.standard === "No" ? (
                            <BlockIcon color="disabled" fontSize="small" />
                          ) : (
                            feature.standard
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {feature.premium === "Yes" ? (
                            <CheckCircleIcon
                              sx={{ color: getTierColor("premium") }}
                              fontSize="small"
                            />
                          ) : feature.premium === "No" ? (
                            <BlockIcon color="disabled" fontSize="small" />
                          ) : (
                            feature.premium
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {feature.elite === "Yes" ? (
                            <CheckCircleIcon
                              sx={{ color: getTierColor("elite") }}
                              fontSize="small"
                            />
                          ) : feature.elite === "No" ? (
                            <BlockIcon color="disabled" fontSize="small" />
                          ) : (
                            feature.elite
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ textAlign: "center" }}>
                <Button
                  variant="contained"
                  onClick={() => setTabValue(1)}
                  size="large"
                  sx={{ bgcolor: "#d44402", "&:hover": { bgcolor: "#b53a00" } }}
                >
                  Choose a Plan
                </Button>
              </Box>
            </Box>
            </Fade>
          )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialogs */}
      <ConfirmationDialog
        open={showCancelConfirmation}
        onClose={handleCancelConfirmationClose}
        onConfirm={handleCancelMembership}
        loading={loading}
        title="Confirm Cancellation"
        message="Are you sure you want to cancel your membership? Your benefits will remain active until the end date, but your membership will not renew automatically."
        confirmText="Cancel Membership"
        cancelText="Keep Membership"
        confirmColor="error"
      />

      <ConfirmationDialog
        open={showUpgradeConfirmation}
        onClose={handleUpgradeConfirmationClose}
        onConfirm={handleChangeTier}
        loading={loading}
        title={`Confirm ${
          selectedTier
            ? selectedTier?.toLowerCase() === USER_ACCOUNT_TYPES.BASIC.value
              ? "Downgrade to Basic"
              : selectedTier.charAt(0).toUpperCase() +
                selectedTier.slice(1) +
                " Tier"
            : "Tier Change"
        }`}
        message={
          isBasic
            ? "You are already on the Basic tier. No changes can be made."
            : selectedTier?.toLowerCase() === USER_ACCOUNT_TYPES.BASIC.value
            ? "Are you sure you want to downgrade to the Basic tier? Your current benefits will remain active until the end of your billing period."
            : `Are you sure you want to change to the ${
                selectedTier?.charAt(0).toUpperCase() +
                  selectedTier?.slice(1) || ""
              } tier? You will be redirected to complete payment.`
        }
        confirmText={
          isBasic ? "OK" : selectedTier?.toLowerCase() === USER_ACCOUNT_TYPES.BASIC.value ? "Downgrade to Basic" : "Confirm Change"
        }
        cancelText={isBasic ? "Close" : "Cancel"}
        confirmColor={isBasic ? "primary" : selectedTier?.toLowerCase() === USER_ACCOUNT_TYPES.BASIC.value ? "warning" : "primary"}
      />

      <ConfirmationDialog
        open={showReactivateConfirmation}
        onClose={handleReactivateConfirmationClose}
        onConfirm={handleReactivateMembership}
        loading={loading}
        title="Confirm Reactivation"
        message="Are you sure you want to reactivate your membership? Your membership will automatically renew when the current period ends."
        confirmText="Reactivate Membership"
        cancelText="Cancel"
        confirmColor="primary"
      />

      <ConfirmationDialog
        open={showAutoRenewConfirmation}
        onClose={handleAutoRenewConfirmationClose}
        onConfirm={handleToggleAutoRenew}
        loading={loading}
        title={isAutoRenew ? "Turn Off Auto-Renewal" : "Turn On Auto-Renewal"}
        message={
          isAutoRenew
            ? "If you turn off auto-renewal, your membership will remain active until the end of your current billing period, but will not automatically renew. You can turn auto-renewal back on at any time."
            : isCanceled
            ? "Turning on auto-renewal will reactivate your canceled membership. Your membership will automatically renew at the end of each billing period. You can turn auto-renewal off at any time."
            : "By turning on auto-renewal, your membership will automatically renew at the end of each billing period. You can turn auto-renewal off at any time."
        }
        confirmText={isAutoRenew ? "Turn Off" : "Turn On"}
        cancelText="Cancel"
        confirmColor={isAutoRenew ? "warning" : "primary"}
      />
    </>
  );
}
